import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ApiError } from "@/lib/api/unwrap";
import { Button } from "@/components/ui/button";
import { DirectoryPanelShell } from "@/components/directory/DirectoryPanelShell";
import { FacetGroup } from "@/components/directory/FacetGroup";
import { FavoritesToggle } from "@/components/directory/FavoritesToggle";
import { MoneyInput } from "@/components/ui/money-input";
import { toast } from "@/components/ui/sonner";
import { useServiceRegionsQuery } from "@/features/expert/api/hooks";
import { useCompanySizes } from "@/features/questionnaire/api";
import { amountToApi } from "@/lib/utils/money";

import { DetailSheet } from "./DetailSheet";
import { FinancialAidCard } from "./FinancialAidCard";
import {
  useAddFinancialAidFavorite,
  useFinancialAidDirectoryQuery,
  useFinancialAidDirectorySectorsQuery,
  useFinancialAidFavoritesQuery,
  useRemoveFinancialAidFavorite,
} from "../../api/hooks";
import type { FinancialAidCard as FinancialAidCardT, FundingType } from "../../types";
import { EmptyState, ErrorState, Loading } from "../ui/states";

/** Aids shown before "see more" reveals the tail (OAC-441: max 10 + "+"). */
const INITIAL_COUNT = 10;

/** EnterpriseProfile.EnterpriseType — the choices the model's array field uses. */
const ORG_TYPES = [
  "self_employed",
  "private",
  "cooperative",
  "not_for_profit",
  "economical_development",
  "municipality",
  "other",
] as const;

const FUNDING_TYPES: FundingType[] = [
  "grant",
  "loan",
  "tax_credit",
  "guarantee",
  "other",
];

interface RefOption {
  id: string;
  name: string;
}

function isApiError(err: unknown): err is ApiError {
  return typeof err === "object" && err !== null && "status" in err;
}

/**
 * The refine rail: search + the harmonized facets (type de financement, régions
 * associées, secteurs associés, taille des entreprises, types d'organisation)
 * + montant minimum + favourites toggle — parity with the Django
 * ``repertoire/_filters.html``. Everything but search & favourites is applied
 * server-side, so both apps narrow through one query.
 */
function AidFilters({
  search,
  onSearch,
  selectedTypes,
  onToggleType,
  regions,
  selectedRegions,
  onToggleRegion,
  sectors,
  selectedSectors,
  onToggleSector,
  companySizes,
  selectedCompanySizes,
  onToggleCompanySize,
  selectedOrgTypes,
  onToggleOrgType,
  minAmount,
  onMinAmount,
  maxAmount,
  onMaxAmount,
  favoriteCount,
  favoritesOnly,
  onFavoritesOnly,
  showFavorites = true,
}: {
  search: string;
  onSearch: (v: string) => void;
  selectedTypes: FundingType[];
  onToggleType: (type: FundingType) => void;
  regions: RefOption[];
  selectedRegions: string[];
  onToggleRegion: (id: string) => void;
  sectors: RefOption[];
  selectedSectors: string[];
  onToggleSector: (id: string) => void;
  companySizes: RefOption[];
  selectedCompanySizes: string[];
  onToggleCompanySize: (id: string) => void;
  selectedOrgTypes: string[];
  onToggleOrgType: (value: string) => void;
  minAmount: string;
  onMinAmount: (v: string) => void;
  maxAmount: string;
  onMaxAmount: (v: string) => void;
  favoriteCount: number;
  favoritesOnly: boolean;
  onFavoritesOnly: (v: boolean) => void;
  /** False when the caller renders the favourites toggle itself (panel chrome). */
  showFavorites?: boolean;
}) {
  const { t } = useTranslation();
  const toggle = (fn: (value: string) => void) => (value: string) => fn(value);
  return (
    <div className="space-y-4">
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={t("financialAids.filters.search")}
        aria-label={t("financialAids.filters.search")}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      {showFavorites && (
        <FavoritesToggle
          count={favoriteCount}
          on={favoritesOnly}
          onChange={onFavoritesOnly}
          label={t("financialAids.filters.favoritesOnly")}
          className="w-full"
        />
      )}
      <div className="overflow-hidden rounded-lg border border-border">
        <FacetGroup
          label={t("financialAids.filters.type")}
          options={FUNDING_TYPES.map((type) => ({
            value: type,
            label: t(`financialAids.types.${type}`),
          }))}
          selected={selectedTypes}
          onToggle={toggle((v) => onToggleType(v as FundingType))}
          defaultOpen
        />
        {regions.length > 0 && (
          <FacetGroup
            label={t("financialAids.filters.regions")}
            options={regions.map((r) => ({ value: r.id, label: r.name }))}
            selected={selectedRegions}
            onToggle={toggle(onToggleRegion)}
          />
        )}
        {sectors.length > 0 && (
          <FacetGroup
            label={t("financialAids.filters.sectorsServed")}
            options={sectors.map((x) => ({ value: x.id, label: x.name }))}
            selected={selectedSectors}
            onToggle={toggle(onToggleSector)}
          />
        )}
        {companySizes.length > 0 && (
          <FacetGroup
            label={t("financialAids.filters.companySizes")}
            options={companySizes.map((x) => ({ value: x.id, label: x.name }))}
            selected={selectedCompanySizes}
            onToggle={toggle(onToggleCompanySize)}
          />
        )}
        <FacetGroup
          label={t("financialAids.filters.orgTypes")}
          options={ORG_TYPES.map((value) => ({
            value,
            label: t(`questionnaire.enterpriseTypes.${value}`),
          }))}
          selected={selectedOrgTypes}
          onToggle={toggle(onToggleOrgType)}
        />
      </div>
      {/* One threshold per figure an aid records, each an "au moins": the floor
          it starts at, and the ceiling it can go up to. Independent, not the two
          ends of one range — see financial_aids.directory. */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label
            htmlFor="fa-min-amount"
            className="block text-sm font-semibold"
          >
            {t("financialAids.filters.minAmount")}
          </label>
          <MoneyInput
            id="fa-min-amount"
            unitId="fa-min-amount-unit"
            value={minAmount}
            onValueChange={onMinAmount}
            className="bg-background text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="fa-max-amount"
            className="block text-sm font-semibold"
          >
            {t("financialAids.filters.maxAmount")}
          </label>
          <MoneyInput
            id="fa-max-amount"
            unitId="fa-max-amount-unit"
            value={maxAmount}
            onValueChange={onMaxAmount}
            className="bg-background text-sm"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Group an ordered list into tier sections. Tiers always read best-first; the
 * manual per-action order an administrator sets shows *inside* each tier.
 */
function groupByTier(aids: FinancialAidCardT[]) {
  const groups: { key: number; label: string; aids: FinancialAidCardT[] }[] = [];
  const byKey = new Map<number, (typeof groups)[number]>();
  for (const aid of aids) {
    let group = byKey.get(aid.relevance_rank);
    if (!group) {
      group = { key: aid.relevance_rank, label: aid.relevance_label, aids: [] };
      byKey.set(aid.relevance_rank, group);
      groups.push(group);
    }
    group.aids.push(aid);
  }
  return groups;
}

export function DirectoryView({
  action,
  favoritesDefault = false,
  variant = "page",
  panelFooterAction,
}: {
  action: string;
  favoritesDefault?: boolean;
  variant?: "page" | "panel";
  /** Escape hatch rendered in the panel footer (ignored on the full page). */
  panelFooterAction?: ReactNode;
}) {
  const { t } = useTranslation();
  const [favoritesOnly, setFavoritesOnly] = useState(favoritesDefault);
  const [showAll, setShowAll] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<FundingType[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [selectedCompanySizes, setSelectedCompanySizes] = useState<string[]>([]);
  const [selectedOrgTypes, setSelectedOrgTypes] = useState<string[]>([]);
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);

  // Server-side étape-5 facets (parity with the Django directory). Search and
  // favourites-only stay client-side (both derivable from the card payload).
  // The box shows a grouped string ("300 000"); the query needs a number.
  const minAmountValue = amountToApi(minAmount);
  const maxAmountValue = amountToApi(maxAmount);
  const serverFilters = useMemo(
    () => ({
      funding_types: selectedTypes,
      service_regions: selectedRegions,
      sectors_served: selectedSectors,
      company_sizes_served: selectedCompanySizes,
      organization_types_served: selectedOrgTypes,
      min_amount: minAmountValue ? Number(minAmountValue) : undefined,
      max_amount: maxAmountValue ? Number(maxAmountValue) : undefined,
    }),
    [
      selectedTypes,
      selectedRegions,
      selectedSectors,
      selectedCompanySizes,
      selectedOrgTypes,
      minAmountValue,
      maxAmountValue,
    ],
  );

  const directory = useFinancialAidDirectoryQuery(action, serverFilters);
  const favoritesQuery = useFinancialAidFavoritesQuery(action);
  const regionsQuery = useServiceRegionsQuery();
  const sectorsQuery = useFinancialAidDirectorySectorsQuery();
  const companySizesQuery = useCompanySizes();
  const addFavorite = useAddFinancialAidFavorite();
  const removeFavorite = useRemoveFinancialAidFavorite();

  const favoriteIdByAid = useMemo(() => {
    const map = new Map<string, string>();
    for (const fav of favoritesQuery.data ?? []) map.set(fav.financial_aid, fav.id);
    return map;
  }, [favoritesQuery.data]);

  const handleToggleFavorite = async (aid: FinancialAidCardT) => {
    if (!action) return;
    setTogglingId(aid.id);
    try {
      if (aid.is_favorite) {
        const favId = favoriteIdByAid.get(aid.id);
        if (favId) await removeFavorite.mutateAsync(favId);
      } else {
        await addFavorite.mutateAsync({ action, aidId: aid.id });
      }
    } catch (err) {
      const detail = isApiError(err) ? err.detail : undefined;
      toast.error(detail ?? t("financialAids.error"));
    } finally {
      setTogglingId(null);
    }
  };

  const toggleType = (type: FundingType) => {
    setShowAll(false);
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type],
    );
  };
  const toggleRegion = (id: string) => {
    setShowAll(false);
    setSelectedRegions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleSector = (id: string) => {
    setShowAll(false);
    setSelectedSectors((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleCompanySize = (id: string) => {
    setShowAll(false);
    setSelectedCompanySizes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleOrgType = (value: string) => {
    setShowAll(false);
    setSelectedOrgTypes((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    );
  };

  if (directory.isPending) return <Loading />;

  if (directory.isError) {
    const status = isApiError(directory.error) ? directory.error.status : 0;
    const message =
      status === 404
        ? t("financialAids.directory.unavailable")
        : isApiError(directory.error)
          ? directory.error.detail
          : undefined;
    const onRetry =
      status >= 400 && status < 500 ? undefined : () => void directory.refetch();
    return <ErrorState message={message} onRetry={onRetry} />;
  }

  const query = search.trim().toLowerCase();
  // Everything the current facets admit, favourites aside. `directory.data` is
  // already narrowed server-side by the type / region / sector / size / org-type
  // facets.
  const matching = directory.data.filter((aid) => {
    if (query && !`${aid.title} ${aid.organization}`.toLowerCase().includes(query))
      return false;
    return true;
  });
  const aids = favoritesOnly ? matching.filter((aid) => aid.is_favorite) : matching;
  // Group first, then cap: a manual per-action order can move an aid across
  // tiers, so the flat list and the rendered page no longer agree on what comes
  // first. Counting rendered rows keeps "the first 10 are shown" true
  // (favorites-only lifts the cap), and matches the Django directory.
  const limit = showAll || favoritesOnly ? Infinity : INITIAL_COUNT;
  const extraCount = favoritesOnly
    ? 0
    : aids.length - Math.min(aids.length, INITIAL_COUNT);
  let rendered = 0;
  const groups = groupByTier(aids)
    .map((group) => ({
      ...group,
      aids: group.aids.filter(() => rendered++ < limit),
    }))
    .filter((group) => group.aids.length > 0);
  // What pressing the ♥ would actually show — the favourites query is unfaceted,
  // so its length can disagree with the list the toggle produces.
  const favoriteCount = matching.filter((aid) => aid.is_favorite).length;
  const hasActiveFilters =
    Boolean(query) ||
    selectedTypes.length > 0 ||
    selectedRegions.length > 0 ||
    selectedSectors.length > 0 ||
    selectedCompanySizes.length > 0 ||
    selectedOrgTypes.length > 0 ||
    Boolean(minAmount) ||
    Boolean(maxAmount);

  const countLabel = favoritesOnly
    ? t("financialAids.directory.favoriteCount", { count: aids.length })
    : t("financialAids.directory.resultCount", { count: aids.length });

  const isPanel = variant === "panel";
  const cardGridClass = isPanel
    ? "grid gap-4"
    : "grid gap-4 sm:grid-cols-2 xl:grid-cols-3";

  const setFavoritesOnlyAndReset = (value: boolean) => {
    setFavoritesOnly(value);
    setShowAll(false);
  };

  const filtersEl = (
    <AidFilters
      search={search}
      onSearch={(v) => {
        setSearch(v);
        setShowAll(false);
      }}
      selectedTypes={selectedTypes}
      onToggleType={toggleType}
      regions={regionsQuery.data ?? []}
      selectedRegions={selectedRegions}
      onToggleRegion={toggleRegion}
      sectors={sectorsQuery.data ?? []}
      selectedSectors={selectedSectors}
      onToggleSector={toggleSector}
      companySizes={companySizesQuery.data ?? []}
      selectedCompanySizes={selectedCompanySizes}
      onToggleCompanySize={toggleCompanySize}
      selectedOrgTypes={selectedOrgTypes}
      onToggleOrgType={toggleOrgType}
      minAmount={minAmount}
      onMinAmount={(v) => {
        setMinAmount(v);
        setShowAll(false);
      }}
      maxAmount={maxAmount}
      onMaxAmount={(v) => {
        setMaxAmount(v);
        setShowAll(false);
      }}
      favoriteCount={favoriteCount}
      favoritesOnly={favoritesOnly}
      onFavoritesOnly={setFavoritesOnlyAndReset}
      // In the panel the ♥ lives in the always-visible toolbar row, so the rail
      // must not render a second copy of the same control.
      showFavorites={!isPanel}
    />
  );

  const resultsBody =
    aids.length === 0 ? (
      <EmptyState
        title={
          favoritesOnly
            ? t("financialAids.directory.noFavorites")
            : hasActiveFilters
              ? t("financialAids.directory.noneFiltered")
              : t("financialAids.directory.none")
        }
      >
        {favoritesOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFavoritesOnly(false);
              setShowAll(false);
            }}
          >
            {t("financialAids.directory.seeAll")}
          </Button>
        )}
      </EmptyState>
    ) : (
      <>
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.key} className="space-y-3">
              {group.label && (
                <h3 className="text-sm font-semibold text-muted-foreground">
                  {group.label}
                </h3>
              )}
              <div className={cardGridClass}>
                {group.aids.map((aid) => (
                  <FinancialAidCard
                    key={aid.id}
                    aid={aid}
                    onOpenDetail={(a) => setDetailId(a.id)}
                    onToggleFavorite={(a) => void handleToggleFavorite(a)}
                    isToggling={togglingId === aid.id}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
        {!showAll && extraCount > 0 && (
          <div className="pt-2 text-center">
            <Button
              variant="outline"
              onClick={() => setShowAll(true)}
              className="gap-2"
            >
              {t("financialAids.directory.seeMore", { count: extraCount })}
              <ChevronDown className="size-4" aria-hidden />
            </Button>
          </div>
        )}
      </>
    );

  const detailAid = detailId
    ? (directory.data.find((a) => a.id === detailId) ?? null)
    : null;
  const detailSheet =
    detailId && detailAid ? (
      <DetailSheet
        action={action}
        aidId={detailId}
        isFavorite={detailAid.is_favorite}
        isToggling={togglingId === detailId}
        onToggleFavorite={(a) => void handleToggleFavorite(a)}
        onClose={() => setDetailId(null)}
      />
    ) : null;

  // Panel chrome: favourites lead the toolbar row, the count trails in the
  // footer alongside the full-directory link.
  if (isPanel) {
    return (
      <>
        <DirectoryPanelShell
          filtersLabel={t("financialAids.filters.heading")}
          filtersOpenByDefault={hasActiveFilters}
          filters={filtersEl}
          favorites={{
            count: favoriteCount,
            on: favoritesOnly,
            onChange: setFavoritesOnlyAndReset,
            label: t("financialAids.filters.favoritesOnly"),
          }}
          countLabel={countLabel}
          footerAction={panelFooterAction}
        >
          {resultsBody}
        </DirectoryPanelShell>
        {detailSheet}
      </>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[17rem_1fr]">
      {/* Sticky and taller than the viewport, the rail only unpins at the bottom of
          its grid row — with several facets open, reaching the last groups meant
          scrolling the page past every result. Let the rail scroll on its own. */}
      <aside className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto">
        {filtersEl}
      </aside>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{countLabel}</p>
        {resultsBody}
      </div>
      {detailSheet}
    </div>
  );
}
